import { memo, useEffect, useRef } from "react";
import { cn } from "../../utils";
import { pad2 } from "../dateCore";
import { PART_CLASSES } from "../dateTimePickerStyles";
import type { DateTimePickerClassNames } from "../types";

export const ROW_HEIGHT = 36;
const VISIBLE_ROWS = 6;

// Memoized: 60 rows would otherwise re-render on every neighbouring tick.
const TimeOption = memo(function TimeOption({
  value,
  active,
  onSelect,
  className,
}: {
  value: number;
  active: boolean;
  onSelect: (value: number) => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      role="option"
      aria-selected={active}
      data-part="time-option"
      style={{ height: ROW_HEIGHT }}
      onClick={() => onSelect(value)}
      className={cn(PART_CLASSES.timeOption, "tabular-nums", className)}
    >
      {pad2(value)}
    </button>
  );
});

interface TimeColumnProps {
  label: string;
  values: number[];
  selected: number;
  onSelect: (value: number) => void;
  classNames?: DateTimePickerClassNames;
}

/** One scrollable wheel of the time panel. */
export function TimeColumn({ label, values, selected, onSelect, classNames }: TimeColumnProps) {
  const listRef = useRef<HTMLDivElement>(null);

  // Scrolling the wheel to the selection is real DOM work, so it stays an effect.
  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const index = values.indexOf(selected);
    if (index >= 0) list.scrollTop = index * ROW_HEIGHT;
  }, [selected, values]);

  return (
    <div
      ref={listRef}
      role="listbox"
      aria-label={label}
      data-part="time-column"
      style={{ height: ROW_HEIGHT * VISIBLE_ROWS }}
      className={cn(
        PART_CLASSES.timeColumn,
        "[scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
        classNames?.timeColumn,
      )}
    >
      {values.map((value) => (
        <TimeOption
          key={value}
          value={value}
          active={value === selected}
          onSelect={onSelect}
          className={classNames?.timeOption}
        />
      ))}
    </div>
  );
}
